<template>
  <ul class="cf-outline__sections">
    <li v-for="section in items" :key="section.key" class="cf-outline__section">
      <button
        type="button"
        class="cf-outline__section-btn"
        :class="{ 'cf-outline__section-btn--current': section.isCurrent }"
        data-cy="cf-outline-section"
        @click="emit('jump-section', section.key)"
      >
        <span
          class="cf-outline__dot"
          :class="[
            section.state && `cf-outline__dot--${section.state}`,
            { 'cf-outline__dot--on': section.isCurrent }
          ]"
        ></span>
        <span
          class="cf-outline__name"
          :class="{ 'cf-outline__name--empty': !section.name }"
          >{{ section.name || "Untitled section" }}</span
        >
        <span class="cf-outline__count">{{ section.count }}</span>
      </button>

      <ul v-if="section.questions.length" class="cf-outline__questions">
        <li v-for="question in section.questions" :key="question.key">
          <button
            type="button"
            class="cf-outline__question"
            :class="{
              'cf-outline__question--active': question.isActive,
              'cf-outline__question--open':
                question.isOpen && !question.isActive
            }"
            data-cy="cf-outline-question"
            @click="emit('jump-question', question.key)"
          >
            <span
              class="cf-outline__tick"
              :class="question.state && `cf-outline__tick--${question.state}`"
              :data-state="question.state"
            ></span>
            <span
              class="cf-outline__name"
              :class="{ 'cf-outline__name--empty': !question.title }"
              >{{ question.title || "Untitled question" }}</span
            >
          </button>
        </li>
      </ul>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { QuestionState } from "./customFormQuestionState";

// The tree both outlines render: the builder marks which question is being
// edited, the submission view colours each node by whether it is done.
export interface OutlineSection {
  key: string | number;
  name: string;
  state?: QuestionState;
  questions: { key: string | number; title: string; state?: QuestionState }[];
}

const props = withDefaults(
  defineProps<{
    sections: OutlineSection[];
    activeQuestionKey?: string | number | null;
    openQuestionKeys?: (string | number)[];
  }>(),
  { activeQuestionKey: null, openQuestionKeys: () => [] }
);

const emit = defineEmits<{
  "jump-section": [key: string | number];
  "jump-question": [key: string | number];
}>();

// The section holding your cursor is "current"; each question is marked active
// (the one you're editing) or open (expanded), so the outline reflects exactly
// what's on the page and lets you jump into any row.
const items = computed(() =>
  props.sections.map((section) => ({
    key: section.key,
    name: section.name,
    state: section.state,
    count: section.questions.length,
    isCurrent: section.questions.some(
      (question) => question.key === props.activeQuestionKey
    ),
    questions: section.questions.map((question) => ({
      key: question.key,
      title: question.title,
      state: question.state,
      isActive: question.key === props.activeQuestionKey,
      isOpen: props.openQuestionKeys.includes(question.key)
    }))
  }))
);
</script>

<style scoped>
.cf-outline__sections {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cf-outline__section {
  margin-bottom: 0.25rem;
}

.cf-outline__section-btn,
.cf-outline__question {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.5rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.cf-outline__section-btn {
  padding: 0.4rem 0.625rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1e293b;
}
.cf-outline__section-btn:hover {
  background: rgb(248 250 252);
}
.cf-outline__section-btn--current {
  color: #1d4ed8;
}

.cf-outline__questions {
  list-style: none;
  margin: 0 0 0.25rem;
  padding: 0;
  border-left: 1px solid rgb(226 232 240);
  margin-left: 0.9rem;
}
.cf-outline__question {
  padding: 0.3rem 0.625rem;
  font-size: 0.8125rem;
  color: #64748b;
}
.cf-outline__question:hover {
  background: rgb(248 250 252);
}
.cf-outline__question--open {
  color: #334155;
  background: rgb(248 250 252);
}
.cf-outline__question--active {
  color: #1d4ed8;
  font-weight: 600;
  background: rgb(239 246 255);
}

.cf-outline__dot {
  flex-shrink: 0;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: transparent;
  box-shadow: inset 0 0 0 1.5px rgb(203 213 225);
}
.cf-outline__dot--on {
  background: #2563eb;
  box-shadow: none;
}
.cf-outline__tick {
  flex-shrink: 0;
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 9999px;
  background: rgb(203 213 225);
}
.cf-outline__question--open .cf-outline__tick {
  background: #60a5fa;
}
.cf-outline__question--active .cf-outline__tick {
  background: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
}

/* Same three states, and the same colours, as the rail beside each answer. */
.cf-outline__dot--satisfied,
.cf-outline__tick--satisfied {
  width: 0.5rem;
  height: 0.5rem;
  background: #22c55e;
  box-shadow: none;
}
.cf-outline__dot--unmet,
.cf-outline__tick--unmet {
  width: 0.5rem;
  height: 0.5rem;
  background: #ef4444;
  box-shadow: none;
}
.cf-outline__dot--optional,
.cf-outline__tick--optional {
  width: 0.5rem;
  height: 0.5rem;
  background: #ffffff;
  box-shadow: inset 0 0 0 1.5px rgb(203 213 225);
}

.cf-outline__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cf-outline__name--empty {
  font-style: italic;
  color: #94a3b8;
}
.cf-outline__count {
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-variant-numeric: tabular-nums;
  color: #94a3b8;
}
</style>
